import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  Calculator, 
  ArrowRight,
  FolderOpen,
  Clock,
  FileText,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  PiggyBank
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { getActivities, formatActivityTime, ActivityEvent } from '../utils/activityTracker';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { memberstack, isAuthenticated } = useAuth();
  const { t } = useI18n();
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [budgetTotals, setBudgetTotals] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    annualSavings: 0
  });

  const loadActivities = async () => {
    if (!memberstack || !isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userActivities = await getActivities(memberstack);
      setActivities(userActivities);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBudgetTotals = async () => {
    if (!memberstack || !isAuthenticated) return;

    try {
      const response = await memberstack.getMemberJSON();
      const budgetData = response.data?.budget || {};
      
      let totalIncome = 0;
      let totalExpenses = 0;

      if (Array.isArray(budgetData)) {
        const incomeCategory = budgetData.find((cat: any) => cat.type === 'income');
        const expenseCategories = budgetData.filter((cat: any) => cat.type === 'expense');

        if (incomeCategory?.items) {
          totalIncome = incomeCategory.items.reduce((sum: number, item: any) => {
            const annual = Number(item.annual) || 0;
            return sum + annual;
          }, 0);
        }

        expenseCategories.forEach((category: any) => {
          if (category.items) {
            totalExpenses += category.items.reduce((sum: number, item: any) => {
              const annual = Number(item.annual) || 0;
              return sum + annual;
            }, 0);
          }
        });
      } else if (Object.keys(budgetData).length > 0) {
        Object.entries(budgetData).forEach(([categoryId, categoryData]: [string, any]) => {
          if (categoryId === 'income') {
            Object.values(categoryData).forEach((item: any) => {
              totalIncome += item.annual || 0;
            });
          } else {
            Object.values(categoryData).forEach((item: any) => {
              totalExpenses += item.annual || 0;
            });
          }
        });
      }

      setBudgetTotals({
        totalIncome,
        totalExpenses,
        annualSavings: totalIncome - totalExpenses
      });
    } catch (error) {
      console.error('Failed to load budget totals:', error);
    }
  };

  useEffect(() => {
    loadActivities();
    loadBudgetTotals();
  }, [memberstack, isAuthenticated]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'ordner_update':
      case 'register_generated':
        return FileText;
      case 'budget_update':
      case 'budget_calculated':
        return Calculator;
      default:
        return Clock;
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">{t('dashboard.title')}</h1>
        </div>

        {/* Available Features */}
        <div className="dashboard-features-card">
          <div className="dashboard-features-grid">
            <div className="dashboard-feature-item dashboard-feature-border-right">
              <div className="dashboard-feature-header">
                <div className="dashboard-icon-box">
                  <FolderOpen className="dashboard-icon" />
                </div>
                <div>
                  <h3 className="dashboard-feature-title">{t('dashboard.folders.title')}</h3>
                  <p className="dashboard-feature-desc">{t('dashboard.folders.description')}</p>
                </div>
              </div>
              <Button 
                className="dashboard-feature-button" 
                onClick={() => navigate('/folders')}
              >
                {t('dashboard.folders.title')}
                <ArrowRight className="icon-sm ml-2" />
              </Button>
            </div>

            <div className="dashboard-feature-item">
              <div className="dashboard-feature-header">
                <div className="dashboard-icon-box">
                  <Calculator className="dashboard-icon" />
                </div>
                <div>
                  <h3 className="dashboard-feature-title">{t('dashboard.budget.title')}</h3>
                  <p className="dashboard-feature-desc">{t('dashboard.budget.description')}</p>
                </div>
              </div>
              <Button 
                className="dashboard-feature-button" 
                onClick={() => navigate('/budget')}
              >
                {t('dashboard.budget.title')}
                <ArrowRight className="icon-sm ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Budget Overview */}
        <div className="dashboard-budget-card">
          <div className="dashboard-budget-grid">
            <div className="dashboard-budget-item dashboard-budget-border-right">
              <div className="dashboard-budget-header">
                <div className="dashboard-icon-box">
                  <TrendingUp className="dashboard-icon" />
                </div>
                <div>
                  <h3 className="dashboard-budget-title">{t('dashboard.income')}</h3>
                </div>
              </div>
              <div className="dashboard-budget-value">
                {budgetTotals.totalIncome.toLocaleString('de-CH')} {t('common.chf')}
              </div>
              <p className="dashboard-budget-label">{t('dashboard.perYear')}</p>
            </div>

            <div className="dashboard-budget-item dashboard-budget-border-right">
              <div className="dashboard-budget-header">
                <div className="dashboard-icon-box">
                  <TrendingDown className="dashboard-icon" />
                </div>
                <div>
                  <h3 className="dashboard-budget-title">{t('dashboard.expenses')}</h3>
                </div>
              </div>
              <div className="dashboard-budget-value">
                {budgetTotals.totalExpenses.toLocaleString('de-CH')} {t('common.chf')}
              </div>
              <p className="dashboard-budget-label">{t('dashboard.perYear')}</p>
            </div>

            <div className="dashboard-budget-item">
              <div className="dashboard-budget-header">
                <div className="dashboard-icon-box">
                  <PiggyBank className="dashboard-icon" />
                </div>
                <div>
                  <h3 className="dashboard-budget-title">{t('dashboard.savings')}</h3>
                </div>
              </div>
              <div className="dashboard-budget-value">
                {budgetTotals.annualSavings.toLocaleString('de-CH')} {t('common.chf')}
              </div>
              <p className="dashboard-budget-label">
                {budgetTotals.annualSavings >= 0 ? t('dashboard.surplus') : t('dashboard.deficit')}
              </p>
            </div>
          </div>
        </div>

        {/* Recent Activities - Compact */}
        <div className="dashboard-activities-card">
          <div className="dashboard-activities-grid">
            <div className="dashboard-activities-section dashboard-activities-border-right">
              <div className="dashboard-activities-header">
                <h3 className="dashboard-activities-title">
                  <Clock className="icon-sm mr-2" />
                  {t('dashboard.activities')}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={loadActivities}
                  disabled={loading}
                  className="font-light"
                >
                  <RefreshCw className={`icon-xs ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              {loading ? (
                <div className="dashboard-loading-center">
                  <div className="dashboard-spinner-sm"></div>
                </div>
              ) : activities.length > 0 ? (
                <div className="dashboard-activities-list">
                  {activities.slice(0, 3).map((activity) => {
                    const IconComponent = getActivityIcon(activity.type);
                    return (
                      <div key={activity.id} className="dashboard-activity-item">
                        <div className="dashboard-activity-icon-box">
                          <IconComponent className="dashboard-activity-icon" />
                        </div>
                        <div className="dashboard-activity-info">
                          <p className="dashboard-activity-action">{activity.action}</p>
                          <p className="dashboard-activity-target">{activity.target}</p>
                        </div>
                        <div className="dashboard-activity-time">
                          {formatActivityTime(activity.timestamp, t)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="dashboard-empty-state">
                  <Clock className="dashboard-empty-icon" />
                  <p className="dashboard-empty-text">{t('dashboard.activities.empty')}</p>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="dashboard-stats-section">
              <h3 className="dashboard-stats-title">{t('dashboard.quickStats')}</h3>
              <div className="dashboard-stats-list">
                <div className="dashboard-stat-row">
                  <span className="dashboard-stat-label">{t('dashboard.monthlySavings')}</span>
                  <span className="dashboard-stat-value">
                    {Math.round(budgetTotals.annualSavings / 12).toLocaleString('de-CH')} {t('common.chf')}
                  </span>
                </div>
                <div className="dashboard-stat-row">
                  <span className="dashboard-stat-label">{t('dashboard.activities')}</span>
                  <span className="dashboard-stat-value">{activities.length}</span>
                </div>
                <div className="dashboard-stat-row">
                  <span className="dashboard-stat-label">{t('dashboard.budgetStatus')}</span>
                  <span className="dashboard-stat-badge">
                    {budgetTotals.annualSavings >= 0 ? t('dashboard.positive') : t('dashboard.negative')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;